import React from 'react';
import { Link, useLocation } from 'react-router-dom';

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items }) => {
  const location = useLocation();

  // Auto-generate breadcrumbs if not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const paths = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Home', path: '/dashboard' }];

    let currentPath = '';
    paths.forEach((path, index) => {
      currentPath += `/${path}`;
      
      // Skip if it's an ID (UUID pattern)
      if (path.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        return;
      }

      // Format the label
      let label = path
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      // Special cases
      if (path === 'retrospectives') label = 'Retrospectives';
      if (path === 'action-items') label = 'Action Items';
      if (path === 'analytics') label = 'Analytics';
      if (path === 'teams') label = 'Teams';
      if (path === 'create') label = 'Create';
      if (path === 'settings') label = 'Settings';

      breadcrumbs.push({
        label,
        path: index === paths.length - 1 ? undefined : currentPath,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items || generateBreadcrumbs();

  // Don't show breadcrumbs on dashboard
  if (location.pathname === '/dashboard' || location.pathname === '/') {
    return null;
  }

  return (
    <nav className="py-3 mb-6 md:py-4 md:mb-8" aria-label="Breadcrumb">
      <ol className="flex items-center flex-wrap gap-2 list-none m-0 p-0 text-sm md:text-base">
        {breadcrumbItems.map((item, index) => (
          <li key={index} className="flex items-center gap-2">
            {item.path ? (
              <>
                <Link
                  to={item.path}
                  className="text-gray-600 font-medium px-2 py-1 rounded hover:text-primary-600 hover:bg-primary-50 transition-colors"
                >
                  {item.label}
                </Link>
                <span className="text-gray-400 select-none">/</span>
              </>
            ) : (
              <span className="text-gray-900 font-semibold px-2 py-1">
                {item.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default Breadcrumb;
